import { $service } from '@xt/client/experimental/request/service'
import { EnumApiCourse } from '@xt/client/api'
import { AjaxBasics } from '@xt/client/helpers'
import moment from 'moment'
import { EnumApiCoupon } from '@xt/client/api/coupon'
import { EnumCouponStatus, EnumCouponType } from '@xt/client/enums'
import { BaseCouponItem } from '@xt/client/types/coupon'
import lodash from 'lodash'
import $global from '@xt/client/store/global'
import type { ClasshourDetail } from '@xt/client/entities/course-v2/types.d'

export enum GoLiveErrorType {
  /**
   * 接口错误
   */
  RequestError,
  /**
   * 用户需要先完善个人信息
   */
  BindUserInfo,
  /**
   * 还没有到上课时间
   */
  LiveNotStart,
  /**
   * 回放还没有生成
   */
  NoPlayback,
  /**
   * 课程结束啦
   */
  CourseEnd
}

export class GoLiveError extends Error {
  public errorType: GoLiveErrorType

  constructor(msg: string, errorType: GoLiveErrorType) {
    super(msg)
    this.errorType = errorType
  }
}

export class ShouldUseCompanionCardError extends Error {
  public companionCardList: BaseCouponItem[]

  constructor(list: BaseCouponItem[]) {
    super('请先使用学习回顾卡')
    this.companionCardList = list
  }
}

export function isCompanionCardCanUse(courseId: number | string, card: BaseCouponItem) {
  const now = AjaxBasics.serviceDate

  // 卡类型不适用
  if (card.couponType !== EnumCouponType.Companion) return false

  // 当前课程不适用
  if (card.exchangeProducts.every(prod => !lodash.isEqual(prod.bizId, courseId))) {
    return false
  }

  // 已过期
  if (now.isAfter(card.validEndTime)) {
    return false
  }

  // 已经卡过期不适用？
  return true
}

export type LiveLink = {
  type: 'miniprogram' | 'web'
  url: string
}

const toLiveLink = (targetRoom: 'playback' | 'live', id: string | number) => {
  if ($global.isEmbeddedH5Page) {
    return {
      type: 'miniprogram',
      url: `/pages/webview/index?p=${encodeURIComponent(`/${targetRoom}?classhour=${id}`)}`
    } satisfies LiveLink
  } else {
    return {
      type: 'web',
      url: `/${targetRoom}?classhour=${id}`
    } satisfies LiveLink
  }
}

// 用户进入直播间的前置逻辑处理
export const getLiveLink = async (classhourId: number, type: 'playback' | 'live', classhourInfo: ClasshourDetail['classhourInfo']) => {
  const { startTime, liveEnd, playbackStatus /** 回放 1， 不回放 2 */, playbackEndTime } = classhourInfo

  // 不能进入直播间
  const preventGotoLive = moment(AjaxBasics.serviceDate).isBefore(moment(startTime).startOf('days'))

  if (preventGotoLive) {
    throw new GoLiveError('还没有到上课时间', GoLiveErrorType.LiveNotStart)
  }

  if (liveEnd === true && playbackStatus === 0) {
    throw new GoLiveError('回放正在生成中...', GoLiveErrorType.NoPlayback)
  }

  // 回放已经结束
  const preventGotoPlayback = playbackEndTime && moment(AjaxBasics.serviceDate).isAfter(moment(playbackEndTime))
  
  if (preventGotoPlayback) {
    // 校验当前课时下是否有已经激活的回放延长卡
    const studyBuffEndTime = classhourInfo.studyBuffEndTime
    const now = AjaxBasics.serviceDate

    if (studyBuffEndTime && now.isBefore(studyBuffEndTime)) {
      return toLiveLink(type, classhourInfo.id)
    }

    // 校验用户是否有可用的回放延长卡
    const [error, packs] = await $service<BaseCouponItem[]>({
      method: 'GET',
      url: EnumApiCoupon.CouponPacks,
      params: {
        couponType: EnumCouponType.Companion,
        status: EnumCouponStatus.UnUsed
      }
    })

    if (error) {
      throw new GoLiveError('用户卡包数据错误', GoLiveErrorType.RequestError)
    }

    const cardList = packs.filter(i => isCompanionCardCanUse(classhourInfo.courseId, i))

    if (cardList.length > 0) {
      throw new ShouldUseCompanionCardError(cardList)
    }

    throw new GoLiveError('课程结束啦', GoLiveErrorType.CourseEnd)
  }

  return toLiveLink(type, classhourInfo.id)
}
